/**
 * Platform roles that can see all sidebar pages and access admin screens.
 * Must match Keycloak realm role names (see keycloak/import/supervity-realm.json).
 */
export const PLATFORM_ADMIN_ROLES = [
  'admin',
  'super_admin',
  'superadmin',
  'quality_admin',
  'quality_manager',
] as const

/** Roles that lift a user out of the "pending approval" gate */
export const APPROVED_ACCESS_ROLES = [
  'user',
  ...PLATFORM_ADMIN_ROLES,
  'Sales_analysis_admin',
  'telecaller',
  'supervisor',
  'manager',
  'process_head',
  'hod',
] as const

export function hasAnyRole(
  roles: string[] | undefined | null,
  allowed: readonly string[]
): boolean {
  if (!roles?.length) return false
  const set = new Set(roles)
  return allowed.some((r) => set.has(r))
}

/** Sidebar + /admin/* pages (all platform admin roles) */
export function hasPlatformAdminAccess(roles?: string[] | null): boolean {
  return hasAnyRole(roles, PLATFORM_ADMIN_ROLES)
}

/**
 * Strict check — admin, super_admin, and quality_admin.
 * Used for: admin sidebar sections, Edit Score button.
 */
export const STRICT_ADMIN_ROLES = ['admin', 'super_admin', 'superadmin', 'quality_admin'] as const

export function hasAdminOrSuperAdminAccess(roles?: string[] | null): boolean {
  return hasAnyRole(roles, STRICT_ADMIN_ROLES)
}

/** Roles allowed to manually upload call recordings on Call Analysis page */
export const CALL_UPLOAD_ROLES = ['super_admin', 'superadmin', 'quality_admin'] as const

export function hasCallUploadAccess(roles?: string[] | null): boolean {
  return hasAnyRole(roles, CALL_UPLOAD_ROLES)
}

export function isPendingUser(roles?: string[] | null): boolean {
  if (!roles?.includes('pending')) return false
  return !hasAnyRole(roles, APPROVED_ACCESS_ROLES)
}
